// ======================================================================
// FILE:
// src/modules/finance/transactions/receipt_voucher/pages/ReceiptVoucherCreatePage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Receipt Voucher - Create
// ======================================================================

import React, {
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
} from "react-router-dom";

import AsyncSelect
    from "react-select/async";

import Select
    from "react-select";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import receiptVoucherService
    from "../services/receiptVoucherService";

import financeLookupService
    from "../../../services/financeLookupService";


// ======================================================================
// OPTIONS
// ======================================================================

const PAYER_TYPE_OPTIONS = [
    {
        value: "BUSINESS_PARTNER",
        label: "Business Partner",
    },
    {
        value: "VENDOR",
        label: "Vendor",
    },
    {
        value: "CUSTOMER",
        label: "Customer",
    },
    {
        value: "EMPLOYEE",
        label: "Employee",
    },
    {
        value: "GOVERNMENT",
        label: "Government / Tax Authority",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];


const RECEIPT_MODE_OPTIONS = [
    {
        value: "BANK_TRANSFER",
        label: "Bank Transfer",
    },
    {
        value: "NEFT",
        label: "NEFT",
    },
    {
        value: "RTGS",
        label: "RTGS",
    },
    {
        value: "IMPS",
        label: "IMPS",
    },
    {
        value: "CHEQUE",
        label: "Cheque",
    },
    {
        value: "CASH",
        label: "Cash",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];


const ALLOCATION_TYPE_OPTIONS = [
    {
        value: "MANUAL",
        label: "Manual",
    },
    {
        value: "INVOICE",
        label: "Invoice",
    },
    {
        value: "ADVANCE",
        label: "Advance",
    },
    {
        value: "TAX",
        label: "Tax",
    },
    {
        value: "OTHER",
        label: "Other",
    },
];


// ======================================================================
// INITIAL STATE
// ======================================================================

const getInitialForm = () => ({
    company_id: null,

    company_option: null,

    voucher_date:
        new Date()
            .toISOString()
            .slice(0, 10),

    receipt_account_id: null,

    receipt_account_option: null,

    payer_type: "",

    payer_id: null,

    payer_option: null,

    payer_name: "",

    receipt_mode: "",

    reference_no: "",

    reference_date: "",

    currency_id: null,

    currency_option: null,

    exchange_rate: "1",

    remarks: "",
});


const getInitialLine = (
    sequenceNo = 1
) => ({
    sequence_no: sequenceNo,

    account_id: null,

    account_option: null,

    allocation_type: "MANUAL",

    business_partner_id: null,

    vendor_id: null,

    customer_id: null,

    employee_id: null,

    transaction_amount: "",

    base_amount: "",

    cost_center_id: null,

    cost_center_option: null,

    project_id: null,

    project_option: null,

    department_id: null,

    description: "",

    source_type: "",

    source_id: null,

    source_reference: "",
});


// ======================================================================
// COMPONENT
// ======================================================================

export default function ReceiptVoucherCreatePage() {

    const navigate =
        useNavigate();


    // ==================================================================
    // STATE
    // ==================================================================

    const [
        formData,
        setFormData
    ] = useState(
        getInitialForm()
    );


    const [
        lines,
        setLines
    ] = useState([
        getInitialLine(1),
    ]);


    const [
        saving,
        setSaving
    ] = useState(false);


    const [
        errorMessage,
        setErrorMessage
    ] = useState("");


    // ==================================================================
    // COMPANY LOOKUP
    // ==================================================================

    async function loadCompanies(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCompanies(
                        inputValue
                    );


            return (
                response || []
            ).map(
                company => ({

                    value:
                        company.id,

                    label:
                        company.company_name,

                    data:
                        company,

                })
            );

        }

        catch (
            error
        ) {

            console.error(
                "Company lookup failed:",
                error
            );

            return [];

        }

    }


    // ==================================================================
    // RECEIPT ACCOUNT LOOKUP
    // ==================================================================

    async function loadReceiptAccounts(
        inputValue = ""
    ) {

        if (
            !formData.company_id
        ) {

            return [];

        }


        try {

            /*
             * Receipt Account uses the existing Finance
             * payment-account lookup because the same
             * cash/bank account master is used by the
             * Receipt Voucher.
             */

            const response =
                await financeLookupService
                    .searchPaymentAccounts(
                        formData.company_id,
                        inputValue,
                        20
                    );


            return (
                response || []
            ).map(
                account => ({

                    ...account,

                    value:
                        account.id,

                    label:
                        `${account.account_name || "Unnamed Account"}`
                        + (
                            account.account_number
                                ? ` - ${account.account_number}`
                                : ""
                        )
                        + (
                            account.branch_name
                                ? ` (${account.branch_name})`
                                : ""
                        ),

                })
            );

        }

        catch (
            error
        ) {

            console.error(
                "Receipt Account lookup failed:",
                error
            );

            return [];

        }

    }


    // ==================================================================
    // VENDOR LOOKUP
    // ======================================================================

    async function loadVendors(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchVendors(
                        inputValue
                    );


            return (
                response || []
            ).map(
                vendor => ({

                    value:
                        vendor.id,

                    label:
                        `${vendor.bp_code || ""} - ${vendor.bp_name || ""}`
                            .replace(
                                /^ - /,
                                ""
                            ),

                    data:
                        vendor,

                })
            );

        }

        catch (
            error
        ) {

            console.error(
                "Vendor lookup failed:",
                error
            );

            return [];

        }

    }


    // ======================================================================
    // CUSTOMER LOOKUP
    // ======================================================================

    async function loadCustomers(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCustomers(
                        inputValue
                    );


            return (
                response || []
            ).map(
                customer => ({

                    value:
                        customer.id,

                    label:
                        `${customer.bp_code || ""} - ${customer.bp_name || ""}`
                            .replace(
                                /^ - /,
                                ""
                            ),

                    data:
                        customer,

                })
            );

        }

        catch (
            error
        ) {

            console.error(
                "Customer lookup failed:",
                error
            );

            return [];

        }

    }


    // ==================================================================
    // CURRENCY LOOKUP
    // ==================================================================

    async function loadCurrencies(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCurrencies(
                        inputValue
                    );


            return (
                response || []
            ).map(
                currency => ({

                    value:
                        currency.id ||
                        currency.value,

                    label:
                        currency.currency_name ||
                        currency.currency_code ||
                        currency.label,

                    data:
                        currency,

                })
            );

        }

        catch (
            error
        ) {

            console.error(
                "Currency lookup failed:",
                error
            );

            return [];

        }

    }


    // ==================================================================
    // ACCOUNT LOOKUP
    // ==================================================================

    async function loadAccounts(
        inputValue = ""
    ) {

        if (
            !formData.company_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService
                    .searchChartOfAccounts(
                        formData.company_id,
                        inputValue,
                        null,
                        20
                    );


            return response || [];

        }

        catch (
            error
        ) {

            console.error(
                "Account lookup failed:",
                error
            );

            return [];

        }

    }
        // ==================================================================
    // COST CENTRE LOOKUP
    // ==================================================================

    async function loadCostCenters(
        inputValue = ""
    ) {

        if (
            !formData.company_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService
                    .searchCostCenters(
                        formData.company_id,
                        inputValue,
                        20
                    );


            return response || [];

        }

        catch (
            error
        ) {

            console.error(
                "Cost centre lookup failed:",
                error
            );

            return [];

        }

    }


    // ==================================================================
    // HEADER CHANGE
    // ==================================================================

    function handleFieldChange(
        field,
        value,
        option = null
    ) {

        setFormData(
            previous => {

                const updated = {

                    ...previous,

                    [field]:
                        value,

                };


                // ----------------------------------------------------------
                // PAYER TYPE
                // ----------------------------------------------------------

                if (
                    field ===
                    "payer_type"
                ) {

                    updated.payer_id =
                        null;

                    updated.payer_option =
                        null;

                    updated.payer_name =
                        "";

                }


                // ----------------------------------------------------------
                // PAYER
                // ----------------------------------------------------------

                if (
                    field ===
                    "payer_id"
                ) {

                    updated.payer_option =
                        option;

                    updated.payer_name =
                        option?.data?.bp_name ||
                        option?.label ||
                        "";

                }


                // ----------------------------------------------------------
                // COMPANY
                // ----------------------------------------------------------

                if (
                    field ===
                    "company_id"
                ) {

                    updated.company_option =
                        option;


                    /*
                     * Receipt Account belongs to Company.
                     *
                     * Therefore changing Company clears the
                     * previously selected Receipt Account.
                     */

                    updated.receipt_account_id =
                        null;

                    updated.receipt_account_option =
                        null;

                }


                // ----------------------------------------------------------
                // RECEIPT ACCOUNT
                // ----------------------------------------------------------

                if (
                    field ===
                    "receipt_account_id"
                ) {

                    updated.receipt_account_option =
                        option;

                }


                // ----------------------------------------------------------
                // CURRENCY
                // ----------------------------------------------------------

                if (
                    field ===
                    "currency_id"
                ) {

                    updated.currency_option =
                        option;

                }


                return updated;

            }
        );


        setErrorMessage("");

    }


    // ==================================================================
    // LINE CHANGE
    // ==================================================================

    function handleLineChange(
        index,
        field,
        value,
        option = null
    ) {

        setLines(
            previous => {

                const updated =
                    [...previous];


                updated[index] = {

                    ...updated[index],

                    [field]:
                        value,

                };


                // ----------------------------------------------------------
                // ACCOUNT
                // ----------------------------------------------------------

                if (
                    field ===
                    "account_id"
                ) {

                    updated[index]
                        .account_option =
                            option;

                }


                // ----------------------------------------------------------
                // COST CENTRE
                // ----------------------------------------------------------

                if (
                    field ===
                    "cost_center_id"
                ) {

                    updated[index]
                        .cost_center_option =
                            option;

                }


                return updated;

            }
        );


        setErrorMessage("");

    }


    // ==================================================================
    // ADD LINE
    // ==================================================================

    function addLine() {

        setLines(
            previous => [

                ...previous,

                getInitialLine(
                    previous.length + 1
                ),

            ]
        );

    }


    // ==================================================================
    // REMOVE LINE
    // ==================================================================

    function removeLine(
        index
    ) {

        if (
            lines.length <= 1
        ) {

            return;

        }


        setLines(
            previous => {

                const updated =
                    previous.filter(
                        (
                            _line,
                            lineIndex
                        ) =>
                            lineIndex !==
                            index
                    );


                return updated.map(
                    (
                        line,
                        lineIndex
                    ) => ({

                        ...line,

                        sequence_no:
                            lineIndex + 1,

                    })
                );

            }
        );

    }


    // ==================================================================
    // TOTALS
    // ==================================================================

    const totalAmount =
        useMemo(
            () =>
                lines.reduce(
                    (
                        total,
                        line
                    ) =>
                        total +
                        (
                            Number(
                                line.transaction_amount
                            ) || 0
                        ),
                    0
                ),
            [lines]
        );


    const totalBaseAmount =
        useMemo(
            () =>
                lines.reduce(
                    (
                        total,
                        line
                    ) =>
                        total +
                        (
                            Number(
                                line.base_amount
                            ) ||
                            (
                                Number(
                                    line.transaction_amount
                                ) *
                                (
                                    Number(
                                        formData.exchange_rate
                                    ) || 1
                                )
                            ) ||
                            0
                        ),
                    0
                ),
            [
                lines,
                formData.exchange_rate,
            ]
        );


    // ==================================================================
    // VALIDATION
    // ==================================================================

    function validateForm() {

        const errors = [];


        if (
            !formData.company_id
        ) {

            errors.push(
                "Company is required."
            );

        }


        if (
            !formData.voucher_date
        ) {

            errors.push(
                "Voucher Date is required."
            );

        }


        if (
            !formData.receipt_account_id
        ) {

            errors.push(
                "Receipt Account is required."
            );

        }


        if (
            !formData.payer_type
        ) {

            errors.push(
                "Payer Type is required."
            );

        }


        if (
            !formData.receipt_mode
        ) {

            errors.push(
                "Receipt Mode is required."
            );

        }


        if (
            !formData.currency_id
        ) {

            errors.push(
                "Currency is required."
            );

        }


        if (
            Number(
                formData.exchange_rate
            ) <= 0
        ) {

            errors.push(
                "Exchange Rate must be greater than zero."
            );

        }


        if (
            !lines.length
        ) {

            errors.push(
                "At least one allocation line is required."
            );

        }


        lines.forEach(
            (
                line,
                index
            ) => {

                if (
                    !line.account_id
                ) {

                    errors.push(
                        `GL Account is required for line ${index + 1}.`
                    );

                }


                if (
                    Number(
                        line.transaction_amount
                    ) <= 0
                ) {

                    errors.push(
                        `Amount is required for line ${index + 1}.`
                    );

                }

            }
        );


        return errors;

    }


    // ==================================================================
    // BUILD PAYLOAD
    // ==================================================================

    function buildPayload() {

        const exchangeRate =
            Number(
                formData.exchange_rate
            ) || 1;


        return {

            company_id:
                Number(
                    formData.company_id
                ),

            voucher_date:
                formData.voucher_date,

            receipt_account_id:
                Number(
                    formData.receipt_account_id
                ),

            payer_type:
                formData.payer_type,

            payer_id:
                formData.payer_id
                    ? Number(
                        formData.payer_id
                    )
                    : null,

            payer_name:
                formData.payer_name
                    ?.trim() ||
                null,

            receipt_mode:
                formData.receipt_mode,

            reference_no:
                formData.reference_no
                    ?.trim() ||
                null,

            reference_date:
                formData.reference_date ||
                null,

            currency_id:
                Number(
                    formData.currency_id
                ),

            exchange_rate:
                exchangeRate,

            remarks:
                formData.remarks
                    ?.trim() ||
                null,

            lines:
                lines.map(
                    (
                        line,
                        index
                    ) => {

                        const amount =
                            Number(
                                line.transaction_amount
                            ) || 0;


                        return {

                            sequence_no:
                                index + 1,

                            account_id:
                                Number(
                                    line.account_id
                                ),

                            allocation_type:
                                line.allocation_type ||
                                "MANUAL",

                            business_partner_id:
                                line.business_partner_id
                                    ? Number(
                                        line.business_partner_id
                                    )
                                    : null,

                            vendor_id:
                                line.vendor_id
                                    ? Number(
                                        line.vendor_id
                                    )
                                    : null,

                            customer_id:
                                line.customer_id
                                    ? Number(
                                        line.customer_id
                                    )
                                    : null,

                            employee_id:
                                line.employee_id
                                    ? Number(
                                        line.employee_id
                                    )
                                    : null,

                            transaction_amount:
                                amount,

                            base_amount:
                                Number(
                                    line.base_amount
                                ) ||
                                (
                                    amount *
                                    exchangeRate
                                ),

                            cost_center_id:
                                line.cost_center_id
                                    ? Number(
                                        line.cost_center_id
                                    )
                                    : null,

                            project_id:
                                line.project_id
                                    ? Number(
                                        line.project_id
                                    )
                                    : null,

                            department_id:
                                line.department_id
                                    ? Number(
                                        line.department_id
                                    )
                                    : null,

                            description:
                                line.description
                                    ?.trim() ||
                                null,

                            source_type:
                                line.source_type
                                    ?.trim() ||
                                null,

                            source_id:
                                line.source_id
                                    ? Number(
                                        line.source_id
                                    )
                                    : null,

                            source_reference:
                                line.source_reference
                                    ?.trim() ||
                                null,

                        };

                    }
                ),

        };

    }


    // ==================================================================
    // SAVE
    // ==================================================================

    async function handleSaveDraft() {

        const errors =
            validateForm();


        if (
            errors.length
        ) {

            setErrorMessage(
                errors.join(" ")
            );

            return;

        }


        try {

            setSaving(
                true
            );


            setErrorMessage("");


            const payload =
                buildPayload();


            const response =
                await receiptVoucherService
                    .createReceiptVoucher(
                        payload
                    );


            navigate(
                `/finance/receipt-vouchers/view/${response.id}`
            );

        }

        catch (
            error
        ) {

            console.error(
                "Receipt Voucher create failed:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to create Receipt Voucher."
            );

        }

        finally {

            setSaving(
                false
            );

        }

    }


    // ==================================================================
    // CANCEL
    // ==================================================================

    function handleCancel() {

        navigate(
            "/finance/receipt-vouchers"
        );

    }


    // ==================================================================
    // FORMAT
    // ==================================================================

    function formatAmount(
        value
    ) {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN",
            {
                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2,
            }
        );

    }


    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <>
                    {/* ==========================================================
                PAGE HEADER
            ========================================================== */}

            <FinancePageHeader

                icon="fas fa-money-bill-wave"

                title="Create Receipt Voucher"

                subtitle={
                    "Record money received from a payer."
                }

                breadcrumbs={[

                    {
                        label:
                            "Finance",

                        to:
                            "/finance",
                    },

                    {
                        label:
                            "Receipt Voucher",

                        to:
                            "/finance/receipt-vouchers",
                    },

                    {
                        label:
                            "Create",
                    },

                ]}

                actions={

                    <>

                        <button

                            type="button"

                            className="
                                btn
                                btn-outline-secondary
                                me-2
                            "

                            onClick={
                                handleCancel
                            }

                            disabled={
                                saving
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-times
                                    me-2
                                "
                            />

                            Cancel

                        </button>


                        <button

                            type="button"

                            className="
                                btn
                                btn-primary
                            "

                            onClick={
                                handleSaveDraft
                            }

                            disabled={
                                saving
                            }

                        >

                            {
                                saving ? (

                                    <>

                                        <span
                                            className="
                                                spinner-border
                                                spinner-border-sm
                                                me-2
                                            "
                                        />

                                        Saving...

                                    </>

                                ) : (

                                    <>

                                        <i
                                            className="
                                                fas
                                                fa-save
                                                me-2
                                            "
                                        />

                                        Save Draft

                                    </>

                                )
                            }

                        </button>

                    </>

                }

            />


            {/* ==========================================================
                ERROR
            ========================================================== */}

            {
                errorMessage && (

                    <div
                        className="
                            alert
                            alert-danger
                            mt-3
                        "
                    >

                        <i
                            className="
                                fas
                                fa-exclamation-triangle
                                me-2
                            "
                        />

                        {errorMessage}

                    </div>

                )
            }


            {/* ==========================================================
                RECEIPT VOUCHER DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="card-header"
                >

                    <h5
                        className="mb-0"
                    >

                        <i
                            className="
                                fas
                                fa-file-invoice-dollar
                                me-2
                            "
                        />

                        Receipt Voucher Details

                    </h5>

                </div>


                <div
                    className="card-body"
                >

                    <div
                        className="row g-3"
                    >

                        {/* ==================================================
                            COMPANY
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Company
                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadCompanies
                                }

                                isClearable

                                value={
                                    formData.company_option
                                }

                                onChange={
                                    option => {

                                        handleFieldChange(
                                            "company_id",
                                            option?.value ||
                                            null,
                                            option
                                        );

                                    }
                                }

                                placeholder="
                                    Select Company
                                "

                            />

                        </div>


                        {/* ==================================================
                            VOUCHER DATE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Voucher Date
                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <input

                                type="date"

                                className="form-control"

                                value={
                                    formData.voucher_date
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "voucher_date",
                                            event.target.value
                                        )
                                }

                            />

                        </div>


                        {/* ==================================================
                            RECEIPT ACCOUNT
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Receipt Account
                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <AsyncSelect

                                key={
                                    formData.company_id ||
                                    "receipt-account"
                                }

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadReceiptAccounts
                                }

                                isClearable

                                isDisabled={
                                    !formData.company_id
                                }

                                value={
                                    formData.receipt_account_option
                                }

                                onChange={
                                    option => {

                                        handleFieldChange(
                                            "receipt_account_id",
                                            option?.value ||
                                            null,
                                            option
                                        );

                                    }
                                }

                                placeholder={
                                    formData.company_id
                                        ? "Select Receipt Account"
                                        : "Select Company First"
                                }

                            />

                        </div>


                        {/* ==================================================
                            PAYER TYPE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Payer Type
                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <Select

                                options={
                                    PAYER_TYPE_OPTIONS
                                }

                                isClearable

                                value={
                                    PAYER_TYPE_OPTIONS.find(
                                        option =>
                                            option.value ===
                                            formData.payer_type
                                    ) ||
                                    null
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "payer_type",
                                            option?.value ||
                                            ""
                                        )
                                }

                                placeholder="
                                    Select Payer Type
                                "

                            />

                        </div>


                        {/* ==================================================
                            PAYER
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Payer
                            </label>


                            {
                                formData.payer_type ===
                                "VENDOR" ? (

                                    <AsyncSelect

                                        cacheOptions

                                        defaultOptions

                                        loadOptions={
                                            loadVendors
                                        }

                                        isClearable

                                        value={
                                            formData.payer_option
                                        }

                                        onChange={
                                            option =>
                                                handleFieldChange(
                                                    "payer_id",
                                                    option?.value ||
                                                    null,
                                                    option
                                                )
                                        }

                                        placeholder="
                                            Select Vendor
                                        "

                                    />

                                ) : formData.payer_type ===
                                "CUSTOMER" ? (

                                    <AsyncSelect

                                        cacheOptions

                                        defaultOptions

                                        loadOptions={
                                            loadCustomers
                                        }

                                        isClearable

                                        value={
                                            formData.payer_option
                                        }

                                        onChange={
                                            option =>
                                                handleFieldChange(
                                                    "payer_id",
                                                    option?.value ||
                                                    null,
                                                    option
                                                )
                                        }

                                        placeholder="
                                            Select Customer
                                        "

                                    />

                                ) : (

                                    <input

                                        type="text"

                                        className="form-control"

                                        value={
                                            formData.payer_name
                                        }

                                        onChange={
                                            event =>
                                                handleFieldChange(
                                                    "payer_name",
                                                    event.target.value
                                                )
                                        }

                                        placeholder="
                                            Enter payer name
                                        "

                                    />

                                )
                            }

                        </div>


                        {/* ==================================================
                            RECEIPT MODE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Receipt Mode
                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <Select

                                options={
                                    RECEIPT_MODE_OPTIONS
                                }

                                isClearable

                                value={
                                    RECEIPT_MODE_OPTIONS.find(
                                        option =>
                                            option.value ===
                                            formData.receipt_mode
                                    ) ||
                                    null
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "receipt_mode",
                                            option?.value ||
                                            ""
                                        )
                                }

                                placeholder="
                                    Select Receipt Mode
                                "

                            />

                        </div>


                        {/* ==================================================
                            REFERENCE NO
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Reference No
                            </label>


                            <input

                                type="text"

                                className="form-control"

                                value={
                                    formData.reference_no
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "reference_no",
                                            event.target.value
                                        )
                                }

                                placeholder="
                                    Cheque / UTR / Transaction No
                                "

                            />

                        </div>


                        {/* ==================================================
                            REFERENCE DATE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Reference Date
                            </label>


                            <input

                                type="date"

                                className="form-control"

                                value={
                                    formData.reference_date
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "reference_date",
                                            event.target.value
                                        )
                                }

                            />

                        </div>


                        {/* ==================================================
                            CURRENCY
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Currency
                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadCurrencies
                                }

                                isClearable

                                value={
                                    formData.currency_option
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "currency_id",
                                            option?.value ||
                                            null,
                                            option
                                        )
                                }

                                placeholder="
                                    Select Currency
                                "

                            />

                        </div>


                        {/* ==================================================
                            EXCHANGE RATE
                        ================================================== */}

                        <div
                            className="
                                col-lg-4
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Exchange Rate
                                <span
                                    className="text-danger"
                                >
                                    *
                                </span>

                            </label>


                            <input

                                type="number"

                                step="0.00000001"

                                min="0"

                                className="form-control"

                                value={
                                    formData.exchange_rate
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "exchange_rate",
                                            event.target.value
                                        )
                                }

                            />

                        </div>


                        {/* ==================================================
                            REMARKS
                        ================================================== */}

                        <div
                            className="col-12"
                        >

                            <label
                                className="form-label"
                            >
                                Remarks
                            </label>


                            <textarea

                                className="form-control"

                                rows={3}

                                value={
                                    formData.remarks
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "remarks",
                                            event.target.value
                                        )
                                }

                                placeholder="
                                    Enter remarks
                                "

                            />

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                ALLOCATION DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="
                        card-header
                        d-flex
                        justify-content-between
                        align-items-center
                    "
                >

                    <h5
                        className="mb-0"
                    >

                        <i
                            className="
                                fas
                                fa-list
                                me-2
                            "
                        />

                        Receipt Allocation Details

                    </h5>


                    <button

                        type="button"

                        className="
                            btn
                            btn-sm
                            btn-outline-primary
                        "

                        onClick={
                            addLine
                        }

                    >

                        <i
                            className="
                                fas
                                fa-plus
                                me-1
                            "
                        />

                        Add Line

                    </button>

                </div>


                <div
                    className="card-body p-0"
                >

                    <div
                        className="table-responsive"
                    >

                        <table
                            className="
                                table
                                table-bordered
                                align-middle
                                mb-0
                            "
                        >

                            <thead
                                className="table-light"
                            >

                                <tr>

                                    <th
                                        style={{
                                            width: 55,
                                        }}
                                    >
                                        #
                                    </th>

                                    <th>
                                        GL Account
                                    </th>

                                    <th>
                                        Allocation Type
                                    </th>
                                    <th>
                                        Cost Centre
                                    </th>

                                    <th>
                                        Transaction Amount
                                    </th>

                                    <th>
                                        Base Amount
                                    </th>

                                    <th>
                                        Description
                                    </th>

                                    <th
                                        style={{
                                            width: 70,
                                        }}
                                    />

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    lines.map(
                                        (
                                            line,
                                            index
                                        ) => (

                                            <tr
                                                key={
                                                    index
                                                }
                                            >

                                                {/* ==============================
                                                    SEQUENCE
                                                ============================== */}

                                                <td>

                                                    {
                                                        index + 1
                                                    }

                                                </td>


                                                {/* ==============================
                                                    GL ACCOUNT
                                                ============================== */}

                                                <td
                                                    style={{
                                                        minWidth: 280,
                                                    }}
                                                >

                                                    <AsyncSelect

                                                        cacheOptions

                                                        defaultOptions

                                                        loadOptions={
                                                            loadAccounts
                                                        }

                                                        isClearable
                                                        menuPortalTarget={
                                                            document.body
                                                        }

                                                        menuPosition="fixed"

                                                        styles={{
                                                            menuPortal: base => ({
                                                                ...base,
                                                                zIndex: 9999,
                                                            }),
                                                        }}

                                                        value={
                                                            line.account_option
                                                        }

                                                        onChange={
                                                            option =>
                                                                handleLineChange(
                                                                    index,
                                                                    "account_id",
                                                                    option?.value ||
                                                                    null,
                                                                    option
                                                                )
                                                        }

                                                        placeholder="
                                                            Select Account
                                                        "

                                                    />

                                                </td>


                                                {/* ==============================
                                                    ALLOCATION TYPE
                                                ============================== */}

                                                <td
                                                    style={{
                                                        minWidth: 160,
                                                    }}
                                                >

                                                    <Select

                                                        options={
                                                            ALLOCATION_TYPE_OPTIONS
                                                        }

                                                        value={
                                                            ALLOCATION_TYPE_OPTIONS.find(
                                                                option =>
                                                                    option.value ===
                                                                    line.allocation_type
                                                            ) ||
                                                            null
                                                        }

                                                        onChange={
                                                            option =>
                                                                handleLineChange(
                                                                    index,
                                                                    "allocation_type",
                                                                    option?.value ||
                                                                    "MANUAL"
                                                                )
                                                        }

                                                            menuPortalTarget={
                                                                document.body
                                                            }

                                                            menuPosition="fixed"

                                                            styles={{
                                                                menuPortal: base => ({
                                                                    ...base,
                                                                    zIndex: 9999,
                                                                }),
                                                            }}

                                                    />

                                                </td>
                                                <td>
                                                    <AsyncSelect
                                                        cacheOptions
                                                        defaultOptions
                                                        loadOptions={loadCostCenters}
                                                        value={line.cost_center_option || null}
                                                        onChange={(option) =>
                                                            handleLineChange(
                                                                index,
                                                                "cost_center_id",
                                                                option ? option.value : null,
                                                                option
                                                            )
                                                        }
                                                        isClearable
                                                        isDisabled={!formData.company_id}
                                                        placeholder="Select Cost Centre"
                                                        menuPortalTarget={document.body}
                                                        styles={{
                                                            menuPortal: (base) => ({
                                                                ...base,
                                                                zIndex: 9999,
                                                            }),
                                                        }}
                                                    />
                                                </td>


                                                {/* ==============================
                                                    TRANSACTION AMOUNT
                                                ============================== */}

                                                <td
                                                    style={{
                                                        minWidth: 150,
                                                    }}
                                                >

                                                    <input

                                                        type="number"

                                                        min="0"

                                                        step="0.01"

                                                        className="
                                                            form-control
                                                            text-end
                                                        "

                                                        value={
                                                            line.transaction_amount
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "transaction_amount",
                                                                    event.target.value
                                                                )
                                                        }

                                                    />

                                                </td>


                                                {/* ==============================
                                                    BASE AMOUNT
                                                ============================== */}

                                                <td
                                                    style={{
                                                        minWidth: 150,
                                                    }}
                                                >

                                                    <input

                                                        type="number"

                                                        min="0"

                                                        step="0.01"

                                                        className="
                                                            form-control
                                                            text-end
                                                        "

                                                        value={
                                                            line.base_amount
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "base_amount",
                                                                    event.target.value
                                                                )
                                                        }

                                                        placeholder={
                                                            (
                                                                Number(
                                                                    line.transaction_amount
                                                                ) *
                                                                (
                                                                    Number(
                                                                        formData.exchange_rate
                                                                    ) || 1
                                                                )
                                                            ).toFixed(2)
                                                        }

                                                    />

                                                </td>


                                                {/* ==============================
                                                    DESCRIPTION
                                                ============================== */}

                                                <td
                                                    style={{
                                                        minWidth: 220,
                                                    }}
                                                >

                                                    <input

                                                        type="text"

                                                        className="form-control"

                                                        value={
                                                            line.description
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "description",
                                                                    event.target.value
                                                                )
                                                        }

                                                        placeholder="
                                                            Description
                                                        "

                                                    />

                                                </td>


                                                {/* ==============================
                                                    REMOVE
                                                ============================== */}

                                                <td
                                                    className="text-center"
                                                >

                                                    <button

                                                        type="button"

                                                        className="
                                                            btn
                                                            btn-sm
                                                            btn-outline-danger
                                                        "

                                                        onClick={() =>
                                                            removeLine(
                                                                index
                                                            )
                                                        }

                                                        disabled={
                                                            lines.length <= 1
                                                        }

                                                        title="
                                                            Remove line
                                                        "

                                                    >

                                                        <i
                                                            className="
                                                                fas
                                                                fa-trash
                                                            "
                                                        />

                                                    </button>

                                                </td>

                                            </tr>

                                        )
                                    )

                                }

                            </tbody>


                            {/* ======================================================
                                TOTALS
                            ====================================================== */}

                            <tfoot>

                                <tr
                                    className="table-light"
                                >

                                    <td
                                        colSpan={3}
                                        className="
                                            text-end
                                            fw-bold
                                        "
                                    >

                                        Total

                                    </td>


                                    <td
                                        className="
                                            text-end
                                            fw-bold
                                        "
                                    >

                                        {
                                            formatAmount(
                                                totalAmount
                                            )
                                        }

                                    </td>


                                    <td
                                        className="
                                            text-end
                                            fw-bold
                                        "
                                    >

                                        {
                                            formatAmount(
                                                totalBaseAmount
                                            )
                                        }

                                    </td>


                                    <td
                                        colSpan={2}
                                    />

                                </tr>

                            </tfoot>

                        </table>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                FOOTER ACTIONS
            ========================================================== */}

            <div
                className="
                    d-flex
                    justify-content-end
                    gap-2
                    mt-4
                    mb-4
                "
            >

                <button

                    type="button"

                    className="
                        btn
                        btn-outline-secondary
                    "

                    onClick={
                        handleCancel
                    }

                    disabled={
                        saving
                    }

                >

                    Cancel

                </button>


                <button

                    type="button"

                    className="
                        btn
                        btn-primary
                    "

                    onClick={
                        handleSaveDraft
                    }

                    disabled={
                        saving
                    }

                >

                    {
                        saving ? (

                            <>

                                <span
                                    className="
                                        spinner-border
                                        spinner-border-sm
                                        me-2
                                    "
                                />

                                Saving...

                            </>

                        ) : (

                            <>

                                <i
                                    className="
                                        fas
                                        fa-save
                                        me-2
                                    "
                                />

                                Save Draft

                            </>

                        )
                    }

                </button>

            </div>

        </>

    );

}